import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslate } from '@tolgee/react';
import { AppRoute } from '@agentselly/immodossier-shared';
import { useLoaderQuery } from '~/helper/useLoaderQuery';
import { useAppParams } from '~/helper/useAppParams';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { RenderWidgets } from '~/components/widget/RenderWidgets';
import { cockpitActivitiesWidgets } from '~/config/widgets/cockpit/cockpitActivitiesWidgets';
import { useAuthz } from '~/context/AuthzContext';
import { fmtDate, fmtTime, PageHead, V3Page } from '~/components/cockpit/v3';
import {
  flattenActivities,
  groupByDay,
  type CockpitActivity,
  type WidgetProps,
} from '~/components/cockpit/cockpitData';
import {
  getCockpitActivityActor,
  getCockpitActivityTitle,
} from '~/components/widget/cockpit/cockpitActivityDesignDataConfig';

export const Route = createFileRoute(
  AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_ACTIVITIES,
)({
  component: ActivitiesPage,
});

const FILTERS: Record<string, readonly string[] | undefined> = {
  all: undefined,
  contact: [
    'call',
    'email',
    'note',
    'meeting',
    'inquiry',
    'visit',
  ],
  views: [
    'page_view',
    'view',
    'slideshow',
    'tour_360',
    'expose_download',
  ],
  offers: [
    'offer',
    'offer_given',
  ],
};

function ActivitiesPage(): React.ReactElement {
  const params = useAppParams(Route);
  const { t } = useTranslate();
  const { atLeast } = useAuthz();
  const isAdvisor = atLeast('partner');
  const [
    filter,
    setFilter,
  ] = useState('all');

  const query = useLoaderQuery(
    AppRoute.$LANG_$COMPANYID_$CONTACTID_COCKPIT_$PROPERTYID_ACTIVITIES,
    { params },
  );

  if (query.isLoading) return <LoadingWidget />;
  if (query.isError || !query.data) return <ErrorWidget error={query.error} />;

  if (!query.data.v3Redesign) {
    const widgetProps = query.data?.widgetProps || {};
    const widgets = cockpitActivitiesWidgets(widgetProps);
    return (
      <div className="h-full flex flex-col gap-y-6">
        <RenderWidgets widgets={widgets} />
      </div>
    );
  }

  const wp = (query.data.widgetProps || {}) as WidgetProps;
  const all = flattenActivities(wp.cockpitActivitiesDetail?.activities);
  const types = FILTERS[filter];
  const list = types ? all.filter((a) => types.includes(a.type)) : all;
  const labels: Record<string, string> = {
    all: t('cockpit_act_f_all', 'Alle'),
    contact: t('cockpit_act_f_contact', 'Kontakt'),
    views: t('cockpit_act_f_views', 'Aufrufe'),
    offers: t('cockpit_act_f_offers', 'Angebote'),
  };
  const count = (k: string): number =>
    FILTERS[k]
      ? all.filter((a) => FILTERS[k]?.includes(a.type)).length
      : all.length;
  // Open tasks only render when the feed carries a task status (not the case yet).
  const openTasks = all.filter(
    (a: CockpitActivity) =>
      a.type === 'task' &&
      typeof a.data?.status === 'string' &&
      a.data.status !== 'COMPLETED',
  );

  return (
    <V3Page>
      <PageHead
        title={t('cockpit_act_title', 'Aktivitäten')}
        sub={t(
          'cockpit_act_sub',
          'Alles, was rund um Ihre Immobilie passiert.',
        )}
      />
      <div className="cols">
        <section
          className="card"
          data-widget="cockpitActivitiesDetail"
        >
          <div
            className="fch"
            role="group"
            aria-label={t('cockpit_act_title', 'Aktivitäten')}
          >
            {Object.keys(FILTERS).map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={k === filter}
                onClick={() => setFilter(k)}
              >
                {labels[k]}
                <span>{count(k)}</span>
              </button>
            ))}
          </div>
          {list.length === 0 ? (
            <p
              className="mu"
              style={{ marginTop: 16 }}
            >
              {t('cockpit_act_empty', 'Keine Aktivitäten vorhanden.')}
            </p>
          ) : (
            <div style={{ marginTop: 16 }}>
              {groupByDay(
                list.map((a) => ({ ...a, date: String(a.date) })),
              ).map(
                ([
                  day,
                  items,
                ]) => (
                  <div
                    className="tlg"
                    key={day}
                  >
                    <h3>{fmtDate(Number(day) || day)}</h3>
                    <ol className="tll">
                      {items.map((a, i) => {
                        const actor = getCockpitActivityActor(
                          t,
                          a.type,
                          a.person,
                        );
                        return (
                          <li
                            className="tle"
                            key={`${a.date}-${i}`}
                          >
                            <span
                              className="tli"
                              aria-hidden="true"
                            />
                            <span className="t">
                              {getCockpitActivityTitle(t, a.type)}
                            </span>
                            <span className="desc">{actor}</span>
                            <div className="tlm">
                              {fmtTime(Number(a.date))}
                              {isAdvisor && a.email ? (
                                <>
                                  {' · '}
                                  <a href={`mailto:${a.email}`}>{a.email}</a>
                                </>
                              ) : null}
                              {isAdvisor && a.phone ? (
                                <>
                                  {' · '}
                                  <a href={`tel:${a.phone}`}>{a.phone}</a>
                                </>
                              ) : null}
                            </div>
                          </li>
                        );
                      })}
                    </ol>
                  </div>
                ),
              )}
            </div>
          )}
        </section>
        {openTasks.length ? (
          <aside className="side">
            <section className="card">
              <div className="sec">
                <h2>{t('cockpit_act_tasks', 'Offene Aufgaben')}</h2>
              </div>
              {openTasks.map((a, i) => (
                <div
                  className="tk"
                  key={`${a.date}-${i}`}
                >
                  <span
                    className="tli"
                    aria-hidden="true"
                  />
                  <span className="t">
                    {getCockpitActivityTitle(t, a.type)}
                  </span>
                </div>
              ))}
            </section>
          </aside>
        ) : null}
      </div>
    </V3Page>
  );
}
